/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 * Emergent-managed Google Auth context. Session token lives in an httpOnly cookie set by the backend.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';

const AuthContext = createContext({
    user: null,
    loading: true,
    signIn: () => {},
    signInGoogle: () => {},
    signOut: async () => {},
    refresh: async () => {},
});

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await api.get('/auth/me', { withCredentials: true });
            setUser(data);
        } catch (e) {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // If returning from OAuth callback, skip the /me check — AuthCallback exchanges the session first.
        if (window.location.hash?.includes('session_id=')) {
            setLoading(false);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    const signIn = useCallback(() => {
        // Route to the in-app sign-in chooser (Google OR email+password).
        navigate('/signin');
    }, [navigate]);

    const signInGoogle = useCallback(() => {
        // Redirect to Emergent Auth; do NOT hardcode redirect URL.
        const redirectUrl = window.location.origin + '/desk';
        window.location.href =
            `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }, []);

    const signOut = useCallback(async () => {
        try {
            await api.post('/auth/logout', {}, { withCredentials: true });
        } catch {}
        setUser(null);
    }, []);

    return (
        <AuthContext.Provider value={{ user, loading, signIn, signInGoogle, signOut, refresh: checkAuth }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
